import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import ImageUploader from "@/components/ImageUploader";
import {
  GraduationCap, Plus, Edit2, Trash2, Loader2, X, Eye, EyeOff,
  ArrowUp, ArrowDown, FileText as FileIcon,
} from "lucide-react";

const EMPTY = {
  title_ar: "", title_en: "", description_ar: "", description_en: "",
  image_url: "", external_url: "", pdf_url: "",
  button_text_ar: "", button_text_en: "", show_button: false,
  status: "draft", display_order: 0,
  regulatory_frameworks: "",
  is_coming_soon: false,
  show_image: true,
  image_size: "standard",
};

const STATUS_BADGE = {
  draft:     "bg-navy/20 text-navy/70",
  published: "bg-emerald-100 text-emerald-800",
  hidden:    "bg-amber-100 text-amber-800",
};

export default function AdminTrainings() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all"); // all | draft | published | hidden

  const load = () => api.get("/admin/trainings").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const visible = useMemo(
    () => rows.filter((r) => filter === "all" ? true : r.status === filter),
    [rows, filter],
  );

  const save = async () => {
    if (!editing.title_en || !editing.title_ar) { toast.error("Titles required"); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/trainings/${editing.id}`, editing);
      else await api.post("/admin/trainings", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const setStatus = async (t, status) => { try { await api.put(`/admin/trainings/${t.id}/status`, { status }); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title_en}"?`)) return;
    try {
      await api.delete(`/admin/trainings/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const move = async (t, dir) => {
    const list = [...visible].sort((a, b) => a.display_order - b.display_order);
    const idx = list.findIndex((x) => x.id === t.id);
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    [list[idx], list[to]] = [list[to], list[idx]];
    await api.put("/admin/trainings/reorder", { ordered_ids: list.map((x) => x.id) });
    load();
  };

  return (
    <div data-testid="admin-trainings">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <span className="eyebrow">Content</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
            <GraduationCap size={26} className="text-gold" /> Training Management
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
                  className="text-xs px-2 py-1 border border-navy/20 rounded-sm bg-white"
                  data-testid="train-filter">
            <option value="all">All ({rows.length})</option>
            <option value="published">Published ({rows.filter((r) => r.status === "published").length})</option>
            <option value="draft">Draft ({rows.filter((r) => r.status === "draft").length})</option>
            <option value="hidden">Hidden ({rows.filter((r) => r.status === "hidden").length})</option>
          </select>
          <button onClick={() => setEditing({ ...EMPTY, display_order: rows.length })} className="btn-gold" data-testid="train-add">
            <Plus size={14} /> New training
          </button>
        </div>
      </div>
      <span className="divider-gold" />

      <div className="card-elev divide-y divide-navy/10">
        {visible.length === 0 ? (
          <div className="p-8 text-center text-navy/50">No trainings yet — click New training.</div>
        ) : visible.sort((a, b) => a.display_order - b.display_order).map((t) => (
          <div key={t.id} className="p-5 flex items-center gap-4" data-testid={`train-row-${t.id}`}>
            <div className="flex flex-col gap-0.5">
              <button onClick={() => move(t, -1)} className="text-navy/50 hover:text-gold" data-testid={`train-up-${t.id}`}><ArrowUp size={12} /></button>
              <button onClick={() => move(t, +1)} className="text-navy/50 hover:text-gold" data-testid={`train-down-${t.id}`}><ArrowDown size={12} /></button>
            </div>
            {t.image_url ? (
              <img src={t.image_url} alt="" className="w-14 h-14 object-cover rounded-sm border border-navy/10" />
            ) : (
              <div className="w-14 h-14 bg-cream border border-navy/10 rounded-sm flex items-center justify-center text-navy/30 text-[10px]">no img</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-navy font-medium truncate">{t.title_en}</div>
              <div className="text-xs text-navy/60 truncate">{t.title_ar}</div>
            </div>
            <Badge className={STATUS_BADGE[t.status] || ""}>{t.status}</Badge>
            <select value={t.status} onChange={(e) => setStatus(t, e.target.value)}
                    className="text-xs px-2 py-1 border border-navy/20 rounded-sm bg-white"
                    data-testid={`train-status-${t.id}`}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="hidden">Hidden</option>
            </select>
            <button onClick={() => setEditing({ ...EMPTY, ...t })} className="text-navy/70 p-1.5" data-testid={`train-edit-${t.id}`}><Edit2 size={14} /></button>
            <button onClick={() => remove(t)} className="text-red-600/80 p-1.5" data-testid={`train-del-${t.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-3xl mx-auto rounded-md border border-gold/30 p-8 my-6" onClick={(e) => e.stopPropagation()} data-testid="train-editor">
            <div className="flex justify-between">
              <h2 className="text-2xl text-navy">{editing.id ? "Edit training" : "New training"}</h2>
              <button onClick={() => setEditing(null)} className="text-navy/50"><X size={18} /></button>
            </div>
            <span className="divider-gold" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className="field-box" placeholder="Title (AR)" value={editing.title_ar} onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })} data-testid="train-title-ar" />
              <input className="field-box" placeholder="Title (EN)" value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} data-testid="train-title-en" />
              <textarea className="field-box md:col-span-1 min-h-[90px]" placeholder="Description (AR)" value={editing.description_ar || ""} onChange={(e) => setEditing({ ...editing, description_ar: e.target.value })} data-testid="train-desc-ar" />
              <textarea className="field-box md:col-span-1 min-h-[90px]" placeholder="Description (EN)" value={editing.description_en || ""} onChange={(e) => setEditing({ ...editing, description_en: e.target.value })} data-testid="train-desc-en" />
              <div className="md:col-span-2">
                <label className="text-xs text-navy/70 mb-2 block">Cover image (optional)</label>
                <ImageUploader value={editing.image_url || ""} folder="trainings" testId="train-image"
                  onChange={(url) => setEditing({ ...editing, image_url: url || "" })} />
              </div>

              <div className="md:col-span-2 border-t border-navy/10 pt-4">
                <div className="text-xs uppercase tracking-widest text-navy/60 mb-3">Optional CTA</div>
                <label className="flex items-center gap-2 text-navy mb-3">
                  <input type="checkbox" className="w-5 h-5 accent-gold" checked={!!editing.show_button} onChange={(e) => setEditing({ ...editing, show_button: e.target.checked })} data-testid="train-show-button" />
                  Show CTA buttons on the public training card
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input className="field-box" placeholder="External link (https://…)" value={editing.external_url || ""} onChange={(e) => setEditing({ ...editing, external_url: e.target.value })} data-testid="train-external-url" />
                  <input className="field-box" placeholder="PDF link (https://…)" value={editing.pdf_url || ""} onChange={(e) => setEditing({ ...editing, pdf_url: e.target.value })} data-testid="train-pdf-url" />
                  <input className="field-box" placeholder="Button text (AR)" value={editing.button_text_ar || ""} onChange={(e) => setEditing({ ...editing, button_text_ar: e.target.value })} data-testid="train-btn-ar" />
                  <input className="field-box" placeholder="Button text (EN)" value={editing.button_text_en || ""} onChange={(e) => setEditing({ ...editing, button_text_en: e.target.value })} data-testid="train-btn-en" />
                </div>
              </div>

              <select className="field-box" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="train-status">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="hidden">Hidden</option>
              </select>
              <input className="field-box" type="number" placeholder="Display order" value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) || 0 })} data-testid="train-order" />

              <div className="md:col-span-2 border-t border-navy/10 pt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-navy">
                  <input type="checkbox" className="w-5 h-5 accent-gold"
                         checked={!!editing.is_coming_soon}
                         onChange={(e) => setEditing({ ...editing, is_coming_soon: e.target.checked })}
                         data-testid="train-coming-soon" />
                  Coming Soon (shows a badge on the public card)
                </label>
                <label className="flex items-center gap-2 text-navy">
                  <input type="checkbox" className="w-5 h-5 accent-gold"
                         checked={editing.show_image !== false}
                         onChange={(e) => setEditing({ ...editing, show_image: e.target.checked })}
                         data-testid="train-show-image" />
                  Show image on the public card
                </label>
                <div>
                  <label className="text-xs text-navy/70 mb-1 block">Image display size</label>
                  <select className="field-box w-full"
                          value={editing.image_size || "standard"}
                          onChange={(e) => setEditing({ ...editing, image_size: e.target.value })}
                          data-testid="train-image-size">
                    <option value="compact">Compact (21:9)</option>
                    <option value="standard">Standard (16:9)</option>
                    <option value="wide">Wide (3:2)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-navy/70 mb-1 block">
                    Regulatory frameworks (CSV — e.g. <code>pdpl,gdpr</code>)
                  </label>
                  <input className="field-box w-full"
                         placeholder="pdpl,gdpr,uae_pdpl,qatar_pdp"
                         value={editing.regulatory_frameworks || ""}
                         onChange={(e) => setEditing({ ...editing, regulatory_frameworks: e.target.value })}
                         data-testid="train-reg" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} className="btn-gold" data-testid="train-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
              </button>
              <button onClick={() => setEditing(null)} className="btn-outline-navy">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
